import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  Input
} from '@progress/kendo-react-inputs';
import {
  ComboBox, ComboBoxChangeEvent
} from '@progress/kendo-react-dropdowns';
import {
  NumericTextBox
} from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { Notification } from '@progress/kendo-react-notification';
import styles from "./_down-payment-dialog.module.scss";
import {IConfiguration} from "../../../../Data/ConfigurationData";
import {ILookupItems} from "../../../../Interfaces/ILookupItems";
import {
  getFullName, hasValue, Organization, toDollars,
  ApplicationDownPaymentRequest, DownPaymentRequestStatus, DownPaymentTransactionType, LookupItem, Vehicle
} from "@amsanalytics/react-shared-library";
import {useDispatch, useSelector} from "react-redux";
import {AppState} from "../../../../store/rootReducer";
import {
  ApplicationDispatch, SET_LOADING,
  createApplicationDownPaymentRequest, clearNewlyCreatedDownPaymentRequest, getDownPaymentHistories
} from "../../../../store/application/types";
import {Error} from "@progress/kendo-react-labels";
import {getEmployeeName} from "../../../../utils/utils";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { Window } from '@progress/kendo-react-dialogs';
import { WindowActionsBar } from '@progress/kendo-react-dialogs';
import {getApplicant} from "../../../../utils/appUtils";
import {Constants, paymentProcessors} from "../../../../utils/constants";
import { CashDrawer } from '../../../../Interfaces/Common/CashDrawer';
import { paymentProcessorOpensInNewTab } from '../../../../utils/downPaymentUtils';
import { v4 } from 'uuid';
import { CardFields, CardFieldsHandle } from '../../../../payments/CardFields';
import { useAcceptJs } from '../../../../payments/useAcceptJs';
import { tokenMethod, tokenizeErrorText } from '../../../../payments/cardToken';
import { CardBilling, EMPTY_BILLING } from '../../../../payments/cardValidation';
import {
  accountLabel,
  CHARGE_ANSWER_LOST,
  chargeCardRefusal,
  chargeTookNothing,
  depositForCharge,
  DownPaymentCardCharge,
  DownPaymentCardConfig,
  isCardPaySource,
  unfinishedChargeDialogText,
} from '../../../../payments/downPaymentCards';
import { refusalText } from '../../../../payments/cardAnswer';
import {
  chargeDownPaymentCard,
  getDownPaymentCardChargeByRequest,
  getDownPaymentCardConfig,
  refreshDownPaymentCardCharges,
} from '../../../../Data/DownPaymentCardData';
import { viewDownPaymentReceiptReportAsync } from '../../../../Data/DownPaymentReceiptData';

interface AmsLookupForComboBoxDisplayWithId {
  id: string;
  code: string;
  description: string;
}

interface DownPaymentDialogProps {
  onClose(): void;
  vehicle: Vehicle;
  configuration: IConfiguration;
  lookups: ILookupItems;
  downPaymentOnQuote: number;
  balanceOnQuote: number;
  amountDeposited: number;
  /** Opens the Card payments window (A13) in place of this dialog. */
  onOpenCardPayments?(): void;
}

/** What the card charge answered (A8): a result panel, or the form again with a message. */
interface CardChargeOutcome {
  /** The row, when there is one to show. */
  charge?: DownPaymentCardCharge;
  /** Lines shown above the row's own message. */
  notes: string[];
}

const billingFrom = (config: DownPaymentCardConfig | null): CardBilling => ({
  nameOnCard: config?.billTo?.nameOnAccount ?? '',
  street: config?.billTo?.street ?? '',
  city: config?.billTo?.city ?? '',
  state: config?.billTo?.state ?? '',
  zip: config?.billTo?.zip ?? '',
});

export const DownPaymentDialog: React.FC<DownPaymentDialogProps> = (props) => {
  const downPaymentDialogDefaultCodes = props.configuration.downPaymentDialogDefaultCodes;
  const dispatch = useDispatch<ApplicationDispatch>();
  const lookups = props.lookups;
  const currentApp = useSelector((state: AppState) => state.applications.editingApp!);
  const primaryApplicantName = getFullName(getApplicant(currentApp, Constants.roleApplicantId, true)?.customer);
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [selectedPaymentProcessorId, setSelectedPaymentProcessorId] = useState<string | null>(null);
  const [linkOpenedInNewTab, setLinkOpenedInNewTab] = useState<boolean>(false);
  const employees = useSelector((state: AppState) => state.applications.employees);
  const newlyCreatedDownPaymentRequest = useSelector((state: AppState) => state.applications.newlyCreatedDownPaymentRequest);
  const downPaymentRequestError = useSelector((state: AppState) => state.applications.downPaymentRequestError);
  const unpaidBalance = props.downPaymentOnQuote - props.amountDeposited;
  const activeUser = useSelector((state: AppState) => state.applications.loggedInUser?.employee);
  const amsUsers = lookups.amsUsers;
  
  const getDefaultCashDrawer = () : CashDrawer | undefined  => {
    // Finds the cash drawer for the user that is logged in
    // Tries to match based on email first, then employee number
    const amsUserForActiveUser = activeUser && (
      amsUsers.find(amsUser => hasValue(amsUser.emailAddress) && amsUser.emailAddress!.trim().toLowerCase() === activeUser.emailAddress?.trim().toLowerCase())
      ?? amsUsers.find(amsUser => hasValue(amsUser.employeeNumber) && amsUser.employeeNumber!.trim().toLowerCase() === activeUser.employeeNumber?.trim().toLowerCase()));

    // Find the assignment for the active user, then resolve the cash drawer
    const assignment = amsUserForActiveUser && lookups.cashDrawerAssignments.find(cda =>
      hasValue(cda.amsUserAssignedToId) && cda.amsUserAssignedToId.trim().toLowerCase() === amsUserForActiveUser.id?.trim().toLowerCase());
    return assignment && lookups.cashDrawers.find(cd => cd.id === assignment.cashDrawerId);
  }

  const getAmsLookupForComboBoxDisplay = (id: string, amsCode: string | null | undefined, amsDescription: string | null | undefined): AmsLookupForComboBoxDisplayWithId => {
    return {
      id: id,
      code: amsCode ?? "",
      description: `${amsCode ?? ""} - ${amsDescription ?? ""}`
    };
  };

  const getAmsLookupToDisplay = (amsLookup: LookupItem): AmsLookupForComboBoxDisplayWithId => {
    return getAmsLookupForComboBoxDisplay(amsLookup.id, amsLookup.name, amsLookup.description);
  };

  const getAmsLookupsToDisplay = (amsLookups: LookupItem[]): AmsLookupForComboBoxDisplayWithId[] => {
    return amsLookups.map(amsLookup => {
      return getAmsLookupToDisplay(amsLookup);
    })};

  const getAmsLocationsOfSaleToDisplay = (organizations: Organization[]): AmsLookupForComboBoxDisplayWithId[] => {
    return organizations.map(organization => {
      return getAmsLookupForComboBoxDisplay(organization.id, organization.amsCode, organization.description);
    });
  };

  const locationsOfPaymentsToDisplay = getAmsLookupsToDisplay(lookups.locationOfPayments);
  const locationsOfSaleToDisplay = getAmsLocationsOfSaleToDisplay(lookups.organizations.filter(o => hasValue(o.amsCode)));
  const paymentSourcesToDisplay = getAmsLookupsToDisplay(lookups.paymentSources);
  const deliveryMethodsToDisplay = getAmsLookupsToDisplay(lookups.paymentDeliveryMethods);

  const getDefaultPaymentSource = () =>
    paymentSourcesToDisplay.find(lp => lp.code === downPaymentDialogDefaultCodes?.paymentSourceCode);
  const getDefaultDeliveryMethod = () =>
    deliveryMethodsToDisplay.find(lop => lop.code === downPaymentDialogDefaultCodes?.deliveryMethodCode);
  const getDefaultLocationOfPayment = () =>
    locationsOfPaymentsToDisplay.find(dn => dn.code === downPaymentDialogDefaultCodes?.locationOfPaymentCode);
  const getDefaultLocationOfSale = () =>
    locationsOfSaleToDisplay.find(ls => ls.id === currentApp.organizationId);
  
  const [applicationDownPaymentRequest, setApplicationDownPaymentRequest] = useState<ApplicationDownPaymentRequest>({
    id: undefined,
    applicationId: currentApp.id,
    applicationNumberForDownPayments: currentApp.applicationNumberForDownPayments,
    applicantName: primaryApplicantName ?? "",
    transactionType: DownPaymentTransactionType.DownPayment,
    vehicleId: currentApp.vehicleId,
    amountTendered: undefined,
    url: undefined,
    locationOfPaymentId: getDefaultLocationOfPayment()?.id,
    locationOfSaleId: getDefaultLocationOfSale()?.id,
    deliveryMethodId: getDefaultDeliveryMethod()?.id,
    cashDrawerId: getDefaultCashDrawer()?.id,
    paymentSourceId: getDefaultPaymentSource()?.id,
    status: DownPaymentRequestStatus.Unpaid,
    spotVehicleAfterConfirmation: false,
    createdBy: undefined,
    createdDate: undefined
  });

  const desiredPaymentExceedsUnpaidBalance = Math.round((applicationDownPaymentRequest.amountTendered ?? 0) * 100) > Math.round(unpaidBalance * 100);
  const balanceOnQuoteIsZero = props.balanceOnQuote <= 0;

  // ── Card down payments (spec LOS_CardDownPayments A1-A8, A12) ──
  // A2: the card settings for this application and Location of Sale, read on open and whenever Location of Sale changes.
  // The previous Location of Sale's settings stay while the next are read, so the dialog doesn't change mode; nothing
  // can be charged until they arrive.
  const [cardConfig, setCardConfig] = useState<DownPaymentCardConfig | null>(null);
  const [cardConfigLoading, setCardConfigLoading] = useState<boolean>(true);
  const [cardConfigError, setCardConfigError] = useState<string | null>(null);
  const cardConfigRequest = useRef(0);
  const locationOfSaleId = applicationDownPaymentRequest.locationOfSaleId;

  useEffect(() => {
    const request = ++cardConfigRequest.current;
    setCardConfigError(null);
    if (!hasValue(locationOfSaleId)) {
      setCardConfig(null);
      setCardConfigLoading(false);
      return;
    }
    setCardConfigLoading(true);
    const settings = "Card payment settings couldn't be read.";
    getDownPaymentCardConfig(currentApp.id, locationOfSaleId!)
      .then((answer) => {
        if (request !== cardConfigRequest.current) return;
        setCardConfig(answer.kind === 'ok' ? answer.data : null);
        if (answer.kind !== 'ok') setCardConfigError(answer.kind === 'refused' ? refusalText(answer) : settings);
      })
      .catch(() => {
        if (request !== cardConfigRequest.current) return;
        setCardConfig(null);
        setCardConfigError(settings);
      })
      .finally(() => {
        if (request === cardConfigRequest.current) setCardConfigLoading(false);
      });
  }, [currentApp.id, locationOfSaleId]);

  const selectedPaymentSource = lookups.paymentSources.find(ps => ps.id === applicationDownPaymentRequest.paymentSourceId);
  // A1: card mode for a pay source DealerFlow 360 links to card payments that no other processor takes.
  const cardMode = isCardPaySource(cardConfig?.cardPaySources, selectedPaymentSource, paymentProcessors.ams);
  // A12: the application's unfinished card charge (it doesn't depend on Location of Sale).
  const unfinishedCharge = cardConfig?.unfinishedCharge ?? null;
  const acceptJs = useAcceptJs(cardConfig?.scriptUrl, cardMode && cardConfig?.available === true);
  // A4, A12: why Charge card is off; null while the settings or Accept.js are still loading.
  const cardRefusal = cardConfigLoading ? null : chargeCardRefusal(cardConfig, acceptJs.status);

  // A6: the draft's idempotency key. A new one after an answer that charged nothing; kept after a lost answer, so a
  // resend returns the same charge.
  const [clientRequestId, setClientRequestId] = useState<string>(() => v4());
  const [billing, setBilling] = useState<CardBilling>(EMPTY_BILLING);
  const billingEdited = useRef(false);
  const cardFieldsRef = useRef<CardFieldsHandle>(null);
  const [charging, setCharging] = useState<boolean>(false);
  // Shown under the card form: a refusal, a decline, or a lost answer.
  const [cardMessages, setCardMessages] = useState<string[]>([]);
  // A8's result panel.
  const [cardResult, setCardResult] = useState<CardChargeOutcome | null>(null);
  const applicationDownPaymentResponses = useSelector((state: AppState) => state.applications.applicationDownPaymentResponses);
  const [printingReceipt, setPrintingReceipt] = useState<boolean>(false);

  // The billing fields start as the primary applicant's name and address, until the user changes them.
  useEffect(() => {
    if (cardConfig?.billTo && !billingEdited.current) setBilling(billingFrom(cardConfig));
  }, [cardConfig]);

  const changeBilling = useCallback((patch: Partial<CardBilling>) => {
    billingEdited.current = true;
    setBilling(current => ({ ...current, ...patch }));
  }, []);

  // A non-card post to AMS waits for its answer, so a refusal (409: an unfinished card charge, or a card pay source)
  // shows in the dialog like its other errors.
  const [awaitingAmsPost, setAwaitingAmsPost] = useState<boolean>(false);

  // The iframe only renders once a payment URL exists, so it can default to open
  const [showPaymentIframe, setShowPaymentIframe] = useState<boolean>(true);

  // Payment processors that block iframe embedding (X-Frame-Options: sameorigin) are flagged
  // OpenInNewTab on los.PaymentProcessor and need to open in a new tab instead
  const selectedProcessorBlocksIframe = paymentProcessorOpensInNewTab(lookups.paymentProcessors, selectedPaymentProcessorId);

  // When the MakePayment API returns a new request with a URL, sync it into local state and show the iframe (or open in new tab)
  useEffect(() => {
    if (newlyCreatedDownPaymentRequest && hasValue(newlyCreatedDownPaymentRequest.url)) {
      setApplicationDownPaymentRequest(prev => ({
        ...prev,
        id: newlyCreatedDownPaymentRequest.id,
        url: newlyCreatedDownPaymentRequest.url,
        createdBy: newlyCreatedDownPaymentRequest.createdBy,
        createdDate: newlyCreatedDownPaymentRequest.createdDate,
      }));

      if (selectedProcessorBlocksIframe) {
        // Show a dialog with a link to open payment in a new tab since it can't be embedded in an iframe
        setLinkOpenedInNewTab(true);
      } else {
        setShowPaymentIframe(true);
      }
    }
  }, [newlyCreatedDownPaymentRequest, selectedProcessorBlocksIframe]);

  const employeeName = getEmployeeName(employees, applicationDownPaymentRequest?.createdBy);

  const updateDownPayment = (field: keyof ApplicationDownPaymentRequest, value: any): void => {
    // Getting the correct amountTendered. If amountTendered is being updated, we use the value passed in.
    const amountTendered = field === "amountTendered" 
      ? value 
      : applicationDownPaymentRequest.amountTendered;
    // DE (Deposit) until the cumulative total (previously deposited + this payment) covers the full down payment, then DP (DownPayment)
    const cumulativeTotal = props.amountDeposited + (amountTendered ?? 0);
    const transactionType: DownPaymentTransactionType = cumulativeTotal >= props.downPaymentOnQuote
      ? DownPaymentTransactionType.DownPayment // When the cumulative total covers the entire down payment amount
      : DownPaymentTransactionType.Deposit; // When the cumulative total is less than the down payment amount
    
    setApplicationDownPaymentRequest({
      ...applicationDownPaymentRequest,
      transactionType: transactionType,
      [field]: value
    });
  };
  
  const errors: string[] = [];
  if (!hasValue(applicationDownPaymentRequest.cashDrawerId)) {
    errors.push("Cash drawer is required.");
  }
  if (!hasValue(applicationDownPaymentRequest.paymentSourceId)) {
    errors.push("Payment source is required.");
  }
  if (!hasValue(applicationDownPaymentRequest.deliveryMethodId)) {
    errors.push("Delivery method is required.");
  }
  if (!hasValue(applicationDownPaymentRequest.locationOfPaymentId)) {
    errors.push("Location of payment is required.");
  }
  if (!hasValue(applicationDownPaymentRequest.locationOfSaleId)) {
    errors.push("Location of sale is required.");
  }
  if (!hasValue(applicationDownPaymentRequest.amountTendered)) {
    errors.push("Tendered amount is required.");
  }
  if ((applicationDownPaymentRequest.amountTendered ?? 0) <= 0) {
    errors.push("Tendered amount must be greater than 0.")
  }

  // Check if the selected payment source is PayNearMe and the employee doesn't have a PayNearMe username
  const selectedPaymentSourceForValidation = lookups.paymentSources.find(ps => ps.id === applicationDownPaymentRequest.paymentSourceId);
  const isPayNearMeWithoutUsername = selectedPaymentSourceForValidation?.paymentProcessorId?.toLowerCase() === paymentProcessors.payNearMe.toLowerCase()
    && !hasValue(activeUser?.payNearMeUsername);
  if (isPayNearMeWithoutUsername) {
    errors.push("Your employee record does not have a PayNearMe username. Please contact support to have your PayNearMe username configured.");
  }

  const atLeastOneRequiredFieldNeedsInput = !hasValue(applicationDownPaymentRequest.amountTendered)
    || !hasValue(applicationDownPaymentRequest.locationOfPaymentId)
    || !hasValue(applicationDownPaymentRequest.locationOfSaleId)
    || !hasValue(applicationDownPaymentRequest.deliveryMethodId)
    || !hasValue(applicationDownPaymentRequest.cashDrawerId)
    || !hasValue(applicationDownPaymentRequest.paymentSourceId);
  
  const handlePostApplicationDownPaymentRequest = () => {
    if (atLeastOneRequiredFieldNeedsInput || isPayNearMeWithoutUsername) {
      setShowErrors(true);
      return;
    }

    const selectedPaymentSource = lookups.paymentSources.find(ps => ps.id === applicationDownPaymentRequest.paymentSourceId)!;
    
    setShowErrors(false);
    setSelectedPaymentProcessorId(selectedPaymentSource.paymentProcessorId);
    dispatch(createApplicationDownPaymentRequest(applicationDownPaymentRequest, selectedPaymentSource.paymentProcessorId));

    if (selectedPaymentSource?.paymentProcessorId.toLowerCase() === paymentProcessors.ams.toLowerCase()) {
      // Closes once AMS has it (there is no URL to display), or stays open with AMS's or the LOS's refusal.
      setAwaitingAmsPost(true);
    }
  };

  const closeDialog = (isPostingPaymentInBackground: boolean) => {
    dispatch(clearNewlyCreatedDownPaymentRequest());
    props.onClose();
  }

  useEffect(() => {
    if (!awaitingAmsPost) return;
    if (newlyCreatedDownPaymentRequest) {
      setAwaitingAmsPost(false);
      closeDialog(true);
    } else if (downPaymentRequestError) {
      setAwaitingAmsPost(false);
    }
    // closeDialog only dispatches and calls onClose
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingAmsPost, newlyCreatedDownPaymentRequest, downPaymentRequestError]);

  const openCardPayments = () => {
    dispatch(clearNewlyCreatedDownPaymentRequest());
    props.onOpenCardPayments?.();
  }

  /** After a charge with a row: the deposit history and the application's card rows are read again. */
  const refreshAfterCharge = (charge: DownPaymentCardCharge) => {
    void refreshDownPaymentCardCharges(currentApp.id);
    if (charge.status === 'Posted') dispatch(getDownPaymentHistories(currentApp.id));
  }

  /** A8: shows what a charge answered. Declined or NotCharged keeps the form and starts a new request id. */
  const showChargeAnswer = (charge: DownPaymentCardCharge, notes: string[]) => {
    refreshAfterCharge(charge);
    if (chargeTookNothing(charge.status)) {
      setClientRequestId(v4());
      cardFieldsRef.current?.clearCard();
      setCardMessages([...notes, charge.message ?? charge.statusText]);
      return;
    }
    setCardMessages([]);
    setCardResult({ charge, notes });
  }

  /**
   * A8: the charge's answer was lost (no response, or a response without a row). The request id is kept, so sending again
   * returns the same charge; the row is read once to show it if it exists.
   */
  const showLostAnswer = async (lead: string) => {
    let lookup: Awaited<ReturnType<typeof getDownPaymentCardChargeByRequest>> | undefined;
    try {
      lookup = await getDownPaymentCardChargeByRequest(clientRequestId);
    } catch {
      lookup = undefined;
    }
    void refreshDownPaymentCardCharges(currentApp.id);
    if (lookup?.kind === 'ok' && lookup.data?.status) {
      showChargeAnswer(lookup.data, [lead]);
    } else {
      setCardMessages([lead]);
    }
  }

  /** A5-A8: Charge card. Accept.js tokenizes the card in the browser; los-api gets the token only. */
  const handleChargeCard = async () => {
    if (charging || !cardConfig || cardRefusal !== undefined) return;
    setCardMessages([]);
    const cardValid = cardFieldsRef.current?.validate() ?? false;
    if (atLeastOneRequiredFieldNeedsInput || (applicationDownPaymentRequest.amountTendered ?? 0) <= 0) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    if (!cardValid) {
      cardFieldsRef.current?.focusFirstIssue();
      return;
    }

    setCharging(true);
    try {
      let token;
      try {
        token = await cardFieldsRef.current!.tokenize({
          apiLoginId: cardConfig.apiLoginId ?? '',
          clientKey: cardConfig.clientKey ?? '',
          scriptUrl: cardConfig.scriptUrl ?? '',
        });
      } catch (error) {
        // Nothing reached los-api: the card can be sent again with the same request id.
        setCardMessages([tokenizeErrorText(error)]);
        return;
      }

      const { kind: _kind, ...method } = tokenMethod(token, billing);
      let answer: Awaited<ReturnType<typeof chargeDownPaymentCard>>;
      try {
        answer = await chargeDownPaymentCard({
          clientRequestId,
          applicationId: currentApp.id,
          amountTendered: applicationDownPaymentRequest.amountTendered!,
          paymentSourceId: applicationDownPaymentRequest.paymentSourceId!,
          deliveryMethodId: applicationDownPaymentRequest.deliveryMethodId!,
          locationOfPaymentId: applicationDownPaymentRequest.locationOfPaymentId!,
          locationOfSaleId: applicationDownPaymentRequest.locationOfSaleId!,
          cashDrawerId: applicationDownPaymentRequest.cashDrawerId!,
          vehicleId: applicationDownPaymentRequest.vehicleId ?? null,
          spotVehicleAfterConfirmation: applicationDownPaymentRequest.spotVehicleAfterConfirmation,
          method,
        });
      } catch {
        answer = { kind: 'lost' };
      }

      if (answer.kind === 'ok') {
        showChargeAnswer(answer.data, []);
      } else if (answer.kind === 'lost') {
        await showLostAnswer(CHARGE_ANSWER_LOST);
      } else if (answer.status >= 500 && answer.status !== 503) {
        // A server error after the request arrived: the charge may have been made, so it is looked up like a lost answer.
        await showLostAnswer(refusalText(answer));
      } else {
        // Refused before anything was charged (400, 404, 409, 503): the same request id can be sent again.
        setCardMessages(answer.messages);
      }
    } finally {
      setCharging(false);
    }
  }

  const cardAccount = cardConfig ? accountLabel(cardConfig) : undefined;
  const chargeCardDisabled = charging || cardRefusal !== undefined || desiredPaymentExceedsUnpaidBalance || balanceOnQuoteIsZero;
  const resultCharge = cardResult?.charge;
  // A8: a posted charge's receipt, the same one the Down Payments list prints. Its deposit comes with the history that
  // refreshAfterCharge reads again, so Print Receipt waits for it.
  const resultPosted = resultCharge?.status === 'Posted';
  const resultDeposit = resultPosted ? depositForCharge(resultCharge!, applicationDownPaymentResponses) : undefined;

  const printReceipt = async () => {
    if (!resultDeposit) return;
    setPrintingReceipt(true);
    dispatch({ type: SET_LOADING, payload: true });
    try {
      await viewDownPaymentReceiptReportAsync(props.configuration, resultDeposit.id);
    } finally {
      dispatch({ type: SET_LOADING, payload: false });
      setPrintingReceipt(false);
    }
  }

  return (
    <div className={styles.paymentForm}>
      <Window
        // Card mode needs a taller, wider window and A8's result a short one; a new key opens each centred at its size.
        key={cardResult ? 'result' : cardMode ? 'card' : 'standard'}
        title={'Down Payment'}
        modal={true}
        minimizeButton={() => <span></span>}
        maximizeButton={() => <span></span>}
        closeButton={() => <span></span>}
        className={cardResult ? `${styles.window} ${styles.resultWindow}` : cardMode ? `${styles.window} ${styles.cardWindow}` : styles.window}
        initialWidth={cardMode || cardResult ? 560 : undefined}
        initialHeight={cardResult ? 280 : cardMode ? Math.min(900, Math.round(window.innerHeight * 0.95)) : undefined}
        resizable={false}
      >
        {cardResult ?
          // A8's result panel: what happened to the card and in AMS.
          <div className={styles.resultPanel}>
            {cardResult.notes.map((note, key) =>
              <Notification key={key} type={{ style: 'warning', icon: true }} className={styles.notice}>{note}</Notification>
            )}
            {resultCharge &&
              <Notification
                type={{ style: resultCharge.status === 'Posted' ? 'success' : 'warning', icon: true }}
                className={styles.notice}
              >
                <p className="k-paragraph k-font-bold" style={{ margin: '0 0 4px' }}>{resultCharge.statusText}</p>
                <p className="k-paragraph" style={{ margin: 0 }}>{resultCharge.message}</p>
              </Notification>
            }
          </div>
          :
          <>
          <div className={styles.fieldRow}>
            <ComboBox
              style={{width: '100%'}}
              className={styles.input}
              label={'Cash Drawer'}
              valid={hasValue(applicationDownPaymentRequest.cashDrawerId)}
              data={lookups.cashDrawers}
              textField="name"
              suggest={true}
              clearButton={false}
              value={lookups.cashDrawers.find(cd => cd.id === applicationDownPaymentRequest.cashDrawerId?.toString())}
              onChange={(event: ComboBoxChangeEvent) => updateDownPayment("cashDrawerId", (event.value as LookupItem).id)}
            />
          </div>
  
          <div className={styles.fieldRow}>
            <ComboBox
              label={'Payment Source'}
              style={{width: '100%'}}
              className={styles.input}
              valid={hasValue(applicationDownPaymentRequest.paymentSourceId)}
              data={paymentSourcesToDisplay}
              textField="description"
              suggest={true}
              clearButton={false}
              value={paymentSourcesToDisplay.find(ps => ps.id === applicationDownPaymentRequest.paymentSourceId)}
              onChange={(event: ComboBoxChangeEvent) => updateDownPayment("paymentSourceId", (event.value as LookupItem).id)}
              filterable={true}
            />
          </div>
  
          <div className={styles.fieldRow}>
            <ComboBox
              className={styles.input}
              label={'Delivery Method'}
              style={{width: "100%"}}
              valid={hasValue(applicationDownPaymentRequest.deliveryMethodId)}
              data={deliveryMethodsToDisplay}
              textField="description"
              suggest={true}
              clearButton={false}
              value={deliveryMethodsToDisplay.find(ps => ps.id === applicationDownPaymentRequest.deliveryMethodId)}
              onChange={(event: ComboBoxChangeEvent) => updateDownPayment("deliveryMethodId", (event.value as LookupItem).id)}
              filterable={true}
            />
          </div>
  
          <div className={styles.fieldRow}>
            <ComboBox
              label={'Location of Payment'}
              style={{width: "100%"}}
              valid={hasValue(applicationDownPaymentRequest.locationOfPaymentId)}
              data={locationsOfPaymentsToDisplay}
              textField="description"
              suggest={true}
              clearButton={false}
              value={locationsOfPaymentsToDisplay.find(ps => ps.id === applicationDownPaymentRequest.locationOfPaymentId)}
              onChange={(event: ComboBoxChangeEvent) => updateDownPayment("locationOfPaymentId", (event.value as LookupItem).id)}
              className={styles.input}
              filterable={true}
            />
          </div>
  
          <div className={styles.fieldRow}>
            <ComboBox
              label={'Location of Sale'}
              style={{width: "100%"}}
              valid={hasValue(applicationDownPaymentRequest.locationOfSaleId)}
              data={locationsOfSaleToDisplay}
              textField="description"
              suggest={true}
              clearButton={false}
              value={locationsOfSaleToDisplay.find(ps => ps.id === applicationDownPaymentRequest.locationOfSaleId)}
              onChange={(event: ComboBoxChangeEvent) => updateDownPayment("locationOfSaleId", (event.value as LookupItem).id)}
              className={styles.input}
              filterable={true}
            />
          </div>
          <div className={styles.fieldRow}>
            <NumericTextBox
              label={"Tendered"}
              style={{width: "100%"}}
              value={applicationDownPaymentRequest.amountTendered}
              onChange={(e) => updateDownPayment('amountTendered', e.value)}
              valid={!desiredPaymentExceedsUnpaidBalance && !balanceOnQuoteIsZero && hasValue(applicationDownPaymentRequest.amountTendered) && (applicationDownPaymentRequest.amountTendered ?? 0) > 0}
              format="c2"
              spinners={false}
              className={styles.input}
              min={0}
            />
          </div>
          {cardMode &&
            // A1, A5: the card, tokenized in the browser by Accept.js, and its billing name and address.
            <div className={styles.cardSection}>
              {cardAccount && <div className={styles.accountLine}>Authorize.Net account: {cardAccount}</div>}
              <CardFields
                ref={cardFieldsRef}
                billing={billing}
                onBillingChange={changeBilling}
                disabled={charging || typeof cardRefusal === 'string'}
              />
            </div>
          }
          {hasValue(applicationDownPaymentRequest.url) && hasValue(applicationDownPaymentRequest.createdDate) &&
            <div className={styles.fieldRow}>
              <Input
                label={"Created By"}
                style={{width: "100%"}}
                value={`Created by ${employeeName} at ${new Date(applicationDownPaymentRequest.createdDate!).toLocaleString()}`}
                className={styles.input}
                disabled
              />
            </div>
          }
  
          <div className={styles.actionSection}>
            <div className={styles.fieldRow}>
              <label className="payment-form__label">Unpaid Balance:</label>
              <div className="payment-form__value-display">{toDollars(props.downPaymentOnQuote - props.amountDeposited)}</div>
            </div>
  
            <div className={styles.fieldRow}>
              <label className="payment-form__label">Deposited:</label>
              <div className="payment-form__value-display">{toDollars(props.amountDeposited)}</div>
            </div>
          </div>
          </>
        }
        <WindowActionsBar>
          {cardResult ?
            <div className={styles.buttonRow}>
              {resultPosted ?
                // The Down Payments list's Print Receipt, with its name beside the icon.
                <Button
                  className={styles.button}
                  fillMode="flat"
                  themeColor="primary"
                  startIcon={<ReceiptLongIcon />}
                  title='Print Receipt'
                  onClick={printReceipt}
                  disabled={!resultDeposit || printingReceipt}
                >
                  Print Receipt
                </Button>
                : (!resultCharge || resultCharge.unfinished) && props.onOpenCardPayments ?
                <Button className={styles.button} onClick={openCardPayments}>Card payments</Button>
                : <span />
              }
              <Button className={styles.button} themeColor="primary" onClick={() => closeDialog(false)}>
                Close
              </Button>
            </div>
            :
            <>
          {unfinishedCharge &&
            // A12: an unfinished card charge blocks every other down payment until it is finished.
            <Notification type={{ style: 'warning', icon: true }} className={styles.notice}>
              <div className={styles.bannerRow}>
                <span>{unfinishedChargeDialogText(unfinishedCharge)}</span>
                {props.onOpenCardPayments &&
                  <Button size="small" onClick={openCardPayments}>Card payments</Button>
                }
              </div>
            </Notification>
          }
          {cardConfigError &&
            <div style={{width: "100%", display: "flex", flexDirection: "column", marginTop: "10px"}}>
              <Error>{cardConfigError}</Error>
            </div>
          }
          {downPaymentRequestError &&
            <div style={{width: "100%", display: "flex", flexDirection: "column", marginTop: "10px"}}>
              {/* The saga stores a thrown error as is when no answer came back. */}
              <Error>{String(downPaymentRequestError)}</Error>
            </div>
          }
          {cardMode && !unfinishedCharge && typeof cardRefusal === 'string' &&
            // A4: why the card can't be charged here.
            <div style={{width: "100%", display: "flex", flexDirection: "column", marginTop: "10px"}}>
              <Error>{cardRefusal}</Error>
            </div>
          }
          {cardMode && cardMessages.length > 0 &&
            <div style={{width: "100%", display: "flex", flexDirection: "column", marginTop: "10px"}}>
              {cardMessages.map((message, key) => <Error key={key}>{message}</Error>)}
            </div>
          }
          {showErrors &&
            <div style={{width: "100%", display: "flex", flexDirection: "column", marginTop: "10px"}}>
              {errors.map((error, key) => <Error key={key}>{error}</Error>)}
            </div>
          }
          {desiredPaymentExceedsUnpaidBalance &&
            <Error>The amount tendered can't be greater than the unpaid balance- {toDollars(unpaidBalance)}</Error>
          }
          {balanceOnQuoteIsZero &&
            <Error>The entire balance cannot be paid in down payments and deposits. The total of all deposits and down payments cannot equal or exceed the balance on the quote ({toDollars(props.balanceOnQuote)}).</Error>
          }
          <div className={styles.buttonRow}>
            {cardMode ?
              <Button
                className={styles.button}
                themeColor="primary"
                onClick={handleChargeCard}
                disabled={chargeCardDisabled}
              >
                {charging ? 'Charging…' : 'Charge card'}
              </Button>
              :
              <Button
                className={styles.button}
                onClick={handlePostApplicationDownPaymentRequest}
                disabled={desiredPaymentExceedsUnpaidBalance || balanceOnQuoteIsZero || !!unfinishedCharge || awaitingAmsPost}
              >
                {`Post Payment`}
              </Button>
            }
            <Button
              className={styles.button}
              onClick={() => closeDialog(false)}
              disabled={charging}
            >
              Cancel
            </Button>
          </div>
            </>
          }
        </WindowActionsBar>
      </Window>

      {!linkOpenedInNewTab && showPaymentIframe && hasValue(applicationDownPaymentRequest.url) &&
        <Window
          initialWidth={1000}
          initialHeight={1100}
          className={styles.iframeWindow}
          modal={true}
          minimizeButton={() => <span></span>}
          onClose={() => closeDialog(false)}
        >
          <iframe
            className={styles.iframe}
            src={applicationDownPaymentRequest.url}
           />
          <WindowActionsBar>
            <Button
              type="button"
              onClick={() => closeDialog(false)}
            >
              Close
            </Button>
          </WindowActionsBar>
        </Window>
      }

      {linkOpenedInNewTab && hasValue(applicationDownPaymentRequest.url) &&
        <Window
          initialWidth={500}
          initialHeight={350}
          modal={true}
          title={"Payment Link"}
          minimizeButton={() => <span></span>}
          maximizeButton={() => <span></span>}
          onClose={() => closeDialog(false)}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px', gap: '16px' }}>
            <OpenInNewIcon style={{ fontSize: 48, color: '#1976d2' }} />
            <h6 className="k-h6" style={{ margin: 0, textAlign: 'center' }}>
              Payment Link
            </h6>
            <p className="k-paragraph" style={{ margin: 0, textAlign: 'center', color: 'rgba(0, 0, 0, 0.6)' }}>
              Complete the payment by clicking the button below.
            </p>
            <Button
              themeColor="primary"
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(applicationDownPaymentRequest.url!, '_blank', 'noopener,noreferrer')}
            >
              Open Payment Link
            </Button>
          </div>
          <WindowActionsBar>
            <Button
              type="button"
              onClick={() => closeDialog(false)}
            >
              Close
            </Button>
          </WindowActionsBar>
        </Window>
      }
    </div>
  );
};
